// ---------------------------------------------------------------------------
// API client for the FastAPI backend
// ---------------------------------------------------------------------------

import type {
  AddSemanticSourceResponse,
  AddMetricDefinitionResponse,
  ApplyMaskingPlanResponse,
  ApplyNamingStandardsResponse,
  ApprovalResponse,
  AskQuestionResponse,
  AutoProfileAnalysis,
  CaptureLineageResponse,
  CatalogExportResponse,
  ChatResponse,
  CostEstimateResponse,
  DatabaseConnectionParams,
  DbConnectDiscoverResponse,
  DdlValidationResponse,
  DeleteMetricDefinitionResponse,
  ListSemanticSchemasResponse,
  ListSemanticSchemaTablesResponse,
  ListSemanticSourcesResponse,
  SemanticTableSelector,
  DeployModelRequest,
  DeployModelResponse,
  DocumentationResponse,
  ImpactAnalysisResponse,
  ReconcileDeploymentRequest,
  ReconcileDeploymentResponse,
  KeySuggestionsResponse,
  LlmAvailabilityResponse,
  MaskingPlanResponse,
  MetricDefinition,
  ModelingInsightsResponse,
  ProfileQuestionsResponse,
  Project,
  RefineSemanticViewResponse,
  ResumeSessionResponse,
  RestoreSnapshotResponse,
  SemanticLayerExportFormat,
  SemanticLayerExportResponse,
  SemanticRelationship,
  SemanticViewResponse,
  SessionDetail,
  SessionSummary,
  SnapshotDiffResponse,
  SnapshotListResponse,
  SnapshotResponse,
  TargetInfo,
  TargetModelResponse,
  UpdateRelationshipsResponse,
  UploadResult,
  UploadResponse,
  ValidationResult,
} from "./types";

const BASE = "/api";

// Methods the backend's CSRF middleware requires a double-submit token for
// (mirrors SAFE_METHODS in ztd_app/api/core/middleware/csrf.py).
const CSRF_REQUIRED_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const res = await fetch(`${BASE}${url}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(CSRF_REQUIRED_METHODS.has(method)
        ? {
            "X-CSRF-Token":
              document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
          }
        : {}),
      ...init?.headers,
    },
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(body.detail || `Request failed: ${res.status}`);
  }
  return res.json();
}

/** Poll `check()` until it returns non-null, or `timeoutMs` elapses. Used
 * right after upload() on every secondary-scan-gated wizard (Semantic
 * Intelligence, Metadata Curator, Data Guardian) — upload() now only
 * persists the file + registers the scan; the deferred profiling/sheet-
 * detection read comes back once getSession() reports the verdict cleared.
 * With no GuardDuty/Defender plan configured (the common case today), the
 * very first poll already resolves — see docs/malware-scanning-design.md. */
async function pollUntil<T>(
  check: () => Promise<T | null>,
  {
    intervalMs = 1500,
    timeoutMs = 15 * 60 * 1000,
  }: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<T> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const result = await check();
    if (result !== null) return result;
    if (Date.now() > deadline) {
      throw new Error(
        "Timed out waiting for the malware scan to complete. Please try again shortly.",
      );
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

/** Turn a resolved (non-pending_scan) SessionDetail into the classic
 * upload() response shape (UploadResponse | SheetSelectionResponse),
 * throwing for the quarantined/error terminal states. Shared by every
 * scan-gated wizard's upload() wrapper below. */
function resolvedSessionToUploadResult(
  sessionId: string,
  session: SessionDetail,
): UploadResult {
  if (session.step === "quarantined") {
    throw new Error(
      "This file was flagged by a secondary malware scan and has been quarantined for review.",
    );
  }
  if (session.step === "error") {
    throw new Error(session.error || "Could not process the uploaded file.");
  }
  if (session.is_multi_sheet) {
    return {
      session_id: sessionId,
      needs_sheet_selection: true,
      sheets: session.sheets ?? [],
      file_name: session.source_name,
    };
  }
  return {
    session_id: sessionId,
    profile: session.profile,
    preview: session.preview,
  };
}

export const api = {
  health: () => request<{ status: string; version: string }>("/health"),

  llmStatus: () =>
    request<{ has_key: boolean; online: boolean }>("/llm-status"),

  uploadFile: async (file: File, projectId?: string): Promise<UploadResult> => {
    const form = new FormData();
    form.append("file", file);
    if (projectId) form.append("project_id", projectId);
    const res = await fetch(`${BASE}/data-semantic/upload`, {
      method: "POST",
      body: form,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail || "Upload failed");
    }
    const { session_id } = (await res.json()) as { session_id: string };
    const session = await pollUntil(async () => {
      const s = await api.getSession(session_id);
      return s.step === "pending_scan" ? null : s;
    });
    return resolvedSessionToUploadResult(session_id, session);
  },

  checkExistingProfile: (
    projectId: string,
    sourceName: string,
    sourceType: string = "file",
  ) =>
    request<{
      found: boolean;
      run_id?: string;
      source_name?: string;
      source_type?: string;
      created_at?: string;
      completed_at?: string;
      table_count?: number;
      column_count?: number;
      row_count?: number;
      issue_count?: number;
      aggregate_score?: number;
    }>("/data-semantic/check-existing-profile", {
      method: "POST",
      body: JSON.stringify({
        project_id: projectId,
        source_name: sourceName,
        source_type: sourceType,
      }),
    }),

  importProfilerRun: (
    projectId: string,
    runId: string,
  ): Promise<UploadResponse> =>
    request<UploadResponse>("/data-semantic/import-profiler-run", {
      method: "POST",
      body: JSON.stringify({ project_id: projectId, run_id: runId }),
    }),

  selectSheets: (
    sessionId: string,
    sheets: string[],
    sheetContexts?: Record<string, string>,
    sheetKeys?: Record<string, any>,
    projectId?: string,
  ) =>
    request<UploadResponse>("/data-semantic/select-sheets", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        sheets,
        sheet_contexts: sheetContexts,
        sheet_keys: sheetKeys,
        project_id: projectId,
      }),
    }),

  autoProfileAnalysis: (sessionId: string, signal?: AbortSignal) =>
    request<AutoProfileAnalysis>("/auto-profile-analysis", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, message: "" }),
      signal,
    }),

  updateSessionContext: (
    sessionId: string,
    context: Record<string, string | undefined>,
  ) =>
    request<{ status: string; context_keys: string[] }>(
      "/data-semantic/update-context",
      {
        method: "POST",
        body: JSON.stringify({ session_id: sessionId, ...context }),
      },
    ),

  getKnowledgeSkills: () =>
    request<{
      skills: { slug: string; label: string; description?: string }[];
    }>("/data-semantic/knowledge-skills"),

  uploadColumnMetadata: async (
    sessionId: string,
    file: File,
  ): Promise<{
    count: number;
    columns: {
      column_name: string;
      description?: string;
      business_name?: string;
      sensitivity?: string;
      domain?: string;
      notes?: string;
    }[];
  }> => {
    const form = new FormData();
    form.append("session_id", sessionId);
    form.append("file", file);
    const res = await fetch(`${BASE}/data-semantic/session-metadata-upload`, {
      method: "POST",
      body: form,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail || "Metadata upload failed");
    }
    return res.json();
  },

  // v2 DB source is two-step (db-connect → select-tables). We compose them so
  // the caller still gets a one-shot UploadResponse ({session_id, profile,
  // preview}) like the legacy /connect-database did. Only safe when a
  // specific table/query was given — for a blank table_or_query, use
  // connectDatabaseDiscover + selectDatabaseTables instead so the user can
  // pick which table(s) to profile rather than eagerly profiling the whole
  // schema (which is what made this dialog feel slow vs. other agents).
  connectDatabase: async (
    params: DatabaseConnectionParams & { project_id?: string },
  ): Promise<UploadResponse> => {
    const connected = await request<{ session_id: string }>(
      "/data-semantic/db-connect",
      { method: "POST", body: JSON.stringify(params) },
    );
    const table = (params.table_or_query || "").trim();
    return request<UploadResponse>("/data-semantic/select-tables", {
      method: "POST",
      body: JSON.stringify({
        session_id: connected.session_id,
        tables: table ? [table] : [],
      }),
    });
  },

  // Connect + discover tables/views only — does NOT profile anything yet.
  // Use selectDatabaseTables afterward once the user has picked which
  // table(s) to actually profile.
  connectDatabaseDiscover: (
    params: DatabaseConnectionParams & { project_id?: string },
  ) =>
    request<DbConnectDiscoverResponse>("/data-semantic/db-connect", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  selectDatabaseTables: (
    sessionId: string,
    // A plain string means "primary source, default schema" (unchanged
    // behavior); a `SemanticTableSelector` qualifies which connection
    // (`source_id`, from addSemanticSource) and/or schema a table comes
    // from, so tables from more than one source can be merged into one
    // target model.
    tables: (string | SemanticTableSelector)[],
    // `undefined` (arg omitted) -> backend default (SEMANTIC_SAMPLE_SIZE).
    // Explicit `null` -> "All records" (matches the Data Profiler agent's
    // "All" preset) -> sent as the <= 0 wire sentinel the backend maps to
    // an uncapped read.
    sampleSize?: number | null,
  ) =>
    request<UploadResponse>("/data-semantic/select-tables", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        tables,
        sample_size: sampleSize === null ? -1 : sampleSize,
      }),
    }),

  // Connect to an ADDITIONAL source database/schema on an existing session
  // — the returned source_id qualifies entries passed to
  // selectDatabaseTables so a target model can be generated from more than
  // one source database/schema at once.
  addSemanticSource: (
    sessionId: string,
    params: DatabaseConnectionParams & { project_id?: string; label?: string },
  ) =>
    request<AddSemanticSourceResponse>("/data-semantic/add-source", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, ...params }),
    }),

  // The primary connection + every additional source registered on the
  // session, for a source-picker panel.
  listSemanticSources: (sessionId: string) =>
    request<ListSemanticSourcesResponse>(`/data-semantic/sources/${sessionId}`),

  // Schema names available on an already-connected source (primary, or one
  // added via addSemanticSource) — powers a dropdown instead of free text.
  listSemanticSchemas: (sessionId: string, sourceId?: string | null) =>
    request<ListSemanticSchemasResponse>(
      `/data-semantic/schemas/${sessionId}${
        sourceId ? `?source_id=${encodeURIComponent(sourceId)}` : ""
      }`,
    ),

  // Browse tables/views in a DIFFERENT schema on an already-connected
  // source (primary, or one added via addSemanticSource) — reuses stored
  // credentials, doesn't mutate the session's default schema.
  listSemanticSchemaTables: (
    sessionId: string,
    schema: string,
    sourceId?: string | null,
  ) =>
    request<ListSemanticSchemaTablesResponse>(
      "/data-semantic/list-schema-tables",
      {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          schema,
          source_id: sourceId ?? undefined,
        }),
      },
    ),

  // Polled while selectDatabaseTables's request is in flight to render a
  // live "N of M tables sampled" loading bar instead of one opaque spinner.
  getSelectTablesProgress: (sessionId: string) =>
    request<{
      status: "idle" | "profiling" | "profiled";
      tables_total: number;
      tables_done: number;
      current_table: string | null;
    }>(`/data-semantic/select-tables/progress/${sessionId}`),

  connectDatabaseFromProject: async (
    projectId: string,
    tableOrQuery: string,
    connectionType: "source" | "target" = "source",
  ): Promise<UploadResponse> => {
    const connected = await request<{ session_id: string }>(
      "/data-semantic/project-db-tables",
      {
        method: "POST",
        body: JSON.stringify({
          project_id: projectId,
          connection_type: connectionType,
        }),
      },
    );
    return request<UploadResponse>("/data-semantic/select-tables", {
      method: "POST",
      body: JSON.stringify({
        session_id: connected.session_id,
        tables: tableOrQuery ? [tableOrQuery] : [],
      }),
    });
  },

  testConnection: (params: DatabaseConnectionParams) =>
    request<{ success: boolean; message: string }>("/test-db-connection", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  chat: (sessionId: string, message: string) =>
    request<ChatResponse>("/chat", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, message }),
    }),

  generateSemanticView: (
    sessionId: string,
    entityName?: string,
    businessContext?: Record<string, unknown>,
    intent: string = "both",
    targetPlatform: string = "postgres",
  ) =>
    request<SemanticViewResponse>("/data-semantic/generate-model", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        entity_name: entityName,
        business_context: businessContext,
        intent,
        target_platform: targetPlatform,
      }),
    }),

  suggestKeys: (sessionId: string) =>
    request<KeySuggestionsResponse>("/data-semantic/suggest-keys", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    }),

  profileQuestions: (sessionId: string) =>
    request<ProfileQuestionsResponse>("/data-semantic/profile-questions", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    }),

  qaSubmit: (sessionId: string, answers: Record<string, string>) =>
    request<{ ok: boolean }>("/data-semantic/qa-submit", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, answers }),
    }),

  qaFollowup: (sessionId: string, answers: Record<string, string>) =>
    request<import("./types").QAFollowupResponse>(
      "/data-semantic/qa-followup",
      {
        method: "POST",
        body: JSON.stringify({ session_id: sessionId, answers }),
      },
    ),

  refineSemanticView: (sessionId: string, message: string) =>
    request<RefineSemanticViewResponse>("/data-semantic/refine", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, feedback: message }),
    }),

  validateSemanticView: (sessionId: string) =>
    request<ValidationResult>("/data-semantic/validate", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    }),

  /** Add a reusable business-metric definition scoped to an entity
   *  (deterministic, no LLM) — the metric-definition layer. */
  addMetricDefinition: (sessionId: string, metric: MetricDefinition) =>
    request<AddMetricDefinitionResponse>("/data-semantic/add-metric", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, metric }),
    }),

  /** Remove a metric definition from the semantic view. */
  deleteMetricDefinition: (sessionId: string, metricName: string) =>
    request<DeleteMetricDefinitionResponse>("/data-semantic/delete-metric", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        metric_name: metricName,
      }),
    }),

  /** Enforce a consistent naming convention (case style, reserved-word
   *  collisions, target-platform max identifier length) across every
   *  entity/column in the semantic view (deterministic, no LLM). */
  applyNamingStandards: (
    sessionId: string,
    options?: {
      case_style?: "snake_case" | "camelCase" | "PascalCase";
      max_length?: number;
      target_platform?: string;
    },
  ) =>
    request<ApplyNamingStandardsResponse>(
      "/data-semantic/apply-naming-standards",
      {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          case_style: options?.case_style,
          max_length: options?.max_length,
          target_platform: options?.target_platform,
        }),
      },
    ),

  /** Persist deterministic manual edits to one entity (AG-SEMANTIC-04). */
  updateSemanticEntity: (
    sessionId: string,
    entityName: string,
    edits: {
      entity?: Record<string, unknown>;
      columns?: Array<{ technical_name: string; [k: string]: unknown }>;
    },
  ) =>
    request<RefineSemanticViewResponse>("/data-semantic/update-entity", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        entity_name: entityName,
        entity: edits.entity,
        columns: edits.columns,
      }),
    }),

  /** Add a new, empty entity — for target-only tables with no source
   *  counterpart (deterministic, no LLM). */
  addSemanticEntity: (
    sessionId: string,
    entityName: string,
    fields?: {
      entity_type?: string;
      description?: string;
      grain?: string;
      business_purpose?: string;
      primary_keys?: string[];
    },
  ) =>
    request<RefineSemanticViewResponse>("/data-semantic/add-entity", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        entity_name: entityName,
        ...fields,
      }),
    }),

  /** Remove an entity (and any relationships referencing it). */
  deleteSemanticEntity: (sessionId: string, entityName: string) =>
    request<RefineSemanticViewResponse>("/data-semantic/delete-entity", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, entity_name: entityName }),
    }),

  /** Add a new column to an existing entity — for target-only fields with
   *  no source counterpart (deterministic, no LLM). */
  addSemanticColumn: (
    sessionId: string,
    entityName: string,
    column: {
      technical_name: string;
      business_name?: string;
      data_type?: string;
      nullable?: boolean;
      description?: string;
      sensitivity?: string;
      domain?: string;
      semantic_type?: string;
    },
  ) =>
    request<RefineSemanticViewResponse>("/data-semantic/add-column", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        entity_name: entityName,
        column,
      }),
    }),

  /** Remove a column from an entity. */
  deleteSemanticColumn: (
    sessionId: string,
    entityName: string,
    technicalName: string,
  ) =>
    request<RefineSemanticViewResponse>("/data-semantic/delete-column", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        entity_name: entityName,
        technical_name: technicalName,
      }),
    }),

  listSessions: () =>
    request<{ sessions: SessionSummary[] }>("/data-semantic/sessions").then(
      (r) => r.sessions,
    ),

  getSession: (id: string) =>
    request<SessionDetail>(`/data-semantic/session/${id}`),

  deleteSession: (id: string) =>
    request<{ status: string }>(`/data-semantic/session/${id}`, {
      method: "DELETE",
    }),

  supportedTargets: () =>
    request<{ targets: TargetInfo[] }>("/data-semantic/supported-targets"),

  generateTargetModel: (
    sessionId: string,
    target: string,
    schemaName?: string,
  ) =>
    request<TargetModelResponse>("/data-semantic/generate-target-model", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        target,
        schema_name: schemaName,
      }),
    }),

  deployModel: (params: DeployModelRequest) =>
    request<DeployModelResponse>("/data-semantic/deploy", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  reconcileDeployment: (params: ReconcileDeploymentRequest) =>
    request<ReconcileDeploymentResponse>(
      "/data-semantic/reconcile-deployment",
      {
        method: "POST",
        body: JSON.stringify(params),
      },
    ),

  generateDocumentation: (sessionId: string) =>
    request<DocumentationResponse>("/data-semantic/generate-documentation", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    }),

  // ── Semantic Intelligence enhancement surface ───────────
  updateRelationships: (
    sessionId: string,
    relationships: SemanticRelationship[],
  ) =>
    request<UpdateRelationshipsResponse>(
      "/data-semantic/update-relationships",
      {
        method: "POST",
        body: JSON.stringify({ session_id: sessionId, relationships }),
      },
    ),

  snapshotSession: (sessionId: string, label?: string) =>
    request<SnapshotResponse>("/data-semantic/snapshot", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, label }),
    }),

  listSnapshots: (sessionId: string) =>
    request<SnapshotListResponse>(`/data-semantic/snapshots/${sessionId}`),

  restoreSnapshot: (sessionId: string, snapshotId: string) =>
    request<RestoreSnapshotResponse>(
      `/data-semantic/snapshots/${sessionId}/restore`,
      {
        method: "POST",
        body: JSON.stringify({ snapshot_id: snapshotId }),
      },
    ),

  diffSnapshots: (
    sessionId: string,
    fromSnapshotId: string,
    toSnapshotId: string,
  ) =>
    request<SnapshotDiffResponse>(
      `/data-semantic/snapshots/${sessionId}/diff`,
      {
        method: "POST",
        body: JSON.stringify({
          from_snapshot_id: fromSnapshotId,
          to_snapshot_id: toSnapshotId,
        }),
      },
    ),

  modelingInsights: (sessionId: string) =>
    request<ModelingInsightsResponse>("/data-semantic/modeling-insights", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    }),

  impactAnalysis: (
    sessionId: string,
    entityName: string,
    columnName?: string,
  ) =>
    request<ImpactAnalysisResponse>("/data-semantic/impact-analysis", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        entity_name: entityName,
        column_name: columnName,
      }),
    }),

  exportSemanticLayer: (sessionId: string, format: SemanticLayerExportFormat) =>
    request<SemanticLayerExportResponse>(
      "/data-semantic/export-semantic-layer",
      {
        method: "POST",
        body: JSON.stringify({ session_id: sessionId, format }),
      },
    ),

  requestApproval: (sessionId: string, target: string, notes?: string) =>
    request<ApprovalResponse>("/data-semantic/request-approval", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, target, notes }),
    }),

  decideApproval: (
    sessionId: string,
    decision: "approved" | "rejected",
    notes?: string,
  ) =>
    request<ApprovalResponse>("/data-semantic/decide-approval", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, decision, notes }),
    }),

  suggestMaskingPlan: (sessionId: string) =>
    request<MaskingPlanResponse>("/data-semantic/suggest-masking-plan", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    }),

  applyMaskingPlan: (
    sessionId: string,
    entityName: string,
    planOverrides?: Record<string, string>,
  ) =>
    request<ApplyMaskingPlanResponse>("/data-semantic/apply-masking-plan", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        entity_name: entityName,
        plan_overrides: planOverrides,
      }),
    }),

  validateDdl: (sessionId: string, target: string) =>
    request<DdlValidationResponse>("/data-semantic/validate-ddl", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, target }),
    }),

  estimateCost: (sessionId: string, target: string) =>
    request<CostEstimateResponse>("/data-semantic/estimate-cost", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, target }),
    }),

  captureLineage: (sessionId: string) =>
    request<CaptureLineageResponse>("/data-semantic/capture-lineage", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    }),

  exportToCatalog: (
    sessionId: string,
    endpointUrl: string,
    apiKey?: string,
    catalogType: string = "generic_rest",
  ) =>
    request<CatalogExportResponse>("/data-semantic/export-to-catalog", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        endpoint_url: endpointUrl,
        api_key: apiKey,
        catalog_type: catalogType,
      }),
    }),

  askQuestion: (sessionId: string, question: string) =>
    request<AskQuestionResponse>("/data-semantic/ask-question", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, question }),
    }),

  checkLlmAvailability: (sessionId: string) =>
    request<LlmAvailabilityResponse>(
      `/data-semantic/llm-availability/${sessionId}`,
    ),

  // ── Project management ──────────────────────────────────
  createProject: (name: string, description?: string) =>
    request<Project>("/projects", {
      method: "POST",
      body: JSON.stringify({ name, description }),
    }),

  listProjects: () => request<Project[]>("/projects"),

  getProject: (id: string) => request<Project>(`/projects/${id}`),

  updateProject: (id: string, data: { name?: string; description?: string }) =>
    request<Project>(`/projects/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteProject: (id: string) =>
    request<{ status: string }>(`/projects/${id}`, { method: "DELETE" }),

  assignSession: (sessionId: string, projectId: string) =>
    request<{ status: string }>("/projects/assign-session", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, project_id: projectId }),
    }),

  resumeSession: (projectId: string, sessionId: string) =>
    request<ResumeSessionResponse>(
      `/projects/${projectId}/resume/${sessionId}`,
      {
        method: "POST",
      },
    ),

  // ── Data Masking Agent ──────────────────────────────────
  maskingTechniques: () =>
    request<{ techniques: MaskingTechnique[] }>("/masking/techniques"),

  maskingUpload: async (
    file: File,
    projectId?: string,
  ): Promise<MaskingUploadResponse> => {
    const form = new FormData();
    form.append("file", file);
    if (projectId) form.append("project_id", projectId);
    const res = await fetch(`${BASE}/masking/upload`, {
      method: "POST",
      body: form,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail || "Upload failed");
    }
    const { session_id } = (await res.json()) as { session_id: string };
    // upload() now only persists + registers the secondary-scan lock; the
    // deferred sensitivity-analysis/sheet-detection read comes back once
    // maskingGetSession() reports the verdict cleared (a no-op wait with no
    // scanner configured, the common case today).
    const session = await pollUntil(async () => {
      const s = await api.maskingGetSession(session_id);
      return s.step === "pending_scan" ? null : s;
    });
    if (session.step === "quarantined") {
      throw new Error(
        "This file was flagged by a secondary malware scan and has been quarantined for review.",
      );
    }
    if (session.step === "error") {
      throw new Error(session.error || "Could not process the uploaded file.");
    }
    return session;
  },

  maskingGetSession: (sessionId: string) =>
    request<MaskingUploadResponse>(`/masking/session/${sessionId}`),

  maskingSelectSheets: (sessionId: string, sheets: string[]) =>
    request<MaskingSelectSheetsResponse>("/masking/select-sheets", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, sheets }),
    }),

  maskingDbConnect: (params: MaskingDbConnectParams) =>
    request<MaskingUploadResponse | MaskingDbTablesResponse>(
      "/masking/db-connect",
      {
        method: "POST",
        body: JSON.stringify(params),
      },
    ),

  maskingProjectDbTables: (
    projectId: string,
    connectionType: "source" | "target" = "source",
  ) =>
    request<{
      session_id: string;
      source_type: string;
      connection_label: string;
      tables: string[];
      views: string[];
      schema: string;
    }>("/masking/project-db-tables", {
      method: "POST",
      body: JSON.stringify({
        project_id: projectId,
        connection_type: connectionType,
      }),
    }),

  maskingDbSelectTable: (sessionId: string, table: string) =>
    request<MaskingUploadResponse>(
      `/masking/db-select-table?session_id=${sessionId}&table=${table}`,
      {
        method: "POST",
      },
    ),

  // ── Multi-table masking (#603) ──────────────────────────────────
  maskingDbSelectTables: (sessionId: string, tables: string[]) =>
    request<MaskingDbSelectTablesResponse>("/masking/db-select-tables", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, tables }),
    }),

  maskingDetectRelationships: (sessionId: string) =>
    request<MaskingRelationshipsResponse>("/masking/detect-relationships", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    }),

  maskingPreview: (
    sessionId: string,
    columnConfigs: MaskingColumnConfig[],
    sheetName?: string,
    lineageEnabled?: boolean,
    lineageGroups?: string[][],
    dateLinks?: DateLink[],
    tableName?: string,
  ) =>
    request<MaskingPreviewResponse>("/masking/preview", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        column_configs: columnConfigs,
        sheet_name: sheetName,
        table_name: tableName,
        lineage_enabled: lineageEnabled || false,
        lineage_groups:
          lineageGroups && lineageGroups.length > 0 ? lineageGroups : undefined,
        date_links: dateLinks && dateLinks.length > 0 ? dateLinks : undefined,
      }),
    }),

  maskingExecute: (params: MaskingExecuteParams) =>
    request<MaskingExecuteStartResponse>("/masking/execute", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  maskingExecuteStatus: (sessionId: string) =>
    request<MaskingExecuteStatusResponse>(
      `/masking/execute-status/${sessionId}`,
    ),

  maskingDownloadUrl: (sessionId: string, format: string): string =>
    `${BASE}/masking/download/${sessionId}?format=${format}`,

  maskingWriteToDb: (params: MaskingWriteDbParams) =>
    request<MaskingWriteDbResponse>("/masking/write-to-db", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  // ── Masking Config Save/Load ──────────────────────────
  maskingConfigSave: (params: MaskingConfigSaveParams) =>
    request<{ status: string; id: string; name: string }>("/masking/configs", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  maskingConfigList: (schemaFingerprint?: string) =>
    request<{ configs: SavedMaskingConfig[] }>(
      `/masking/configs${schemaFingerprint ? `?schema=${schemaFingerprint}` : ""}`,
    ),

  maskingConfigGet: (id: string) =>
    request<SavedMaskingConfig>(`/masking/configs/${id}`),

  maskingConfigDelete: (id: string) =>
    request<{ status: string; id: string }>(`/masking/configs/${id}`, {
      method: "DELETE",
    }),

  // ── Masking Script Generation ─────────────────────────
  maskingGenerateScript: async (
    params: MaskingGenerateScriptParams,
  ): Promise<{ blob: Blob; tokenInfo: LLMTokenInfo | null }> => {
    const res = await fetch(`${BASE}/masking/generate-script`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      body: JSON.stringify(params),
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail || "Script generation failed");
    }
    const blob = await res.blob();
    // Extract LLM token info from response headers
    const model = res.headers.get("X-LLM-Model");
    let tokenInfo: LLMTokenInfo | null = null;
    if (model) {
      tokenInfo = {
        model,
        input_tokens: parseInt(
          res.headers.get("X-LLM-Input-Tokens") || "0",
          10,
        ),
        output_tokens: parseInt(
          res.headers.get("X-LLM-Output-Tokens") || "0",
          10,
        ),
        total_tokens: parseInt(
          res.headers.get("X-LLM-Total-Tokens") || "0",
          10,
        ),
      };
    }
    return { blob, tokenInfo };
  },

  // ── Masking Token Estimation ──────────────────────────
  maskingEstimateTokens: (params: Record<string, unknown>) =>
    request<LLMTokenEstimate>("/masking/estimate-tokens", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(params),
    }),

  // ── Metadata Catalog Agent ──────────────────────────────
  metadataUpload: async (
    file: File,
    glossaryMode?: string,
    projectId?: string,
  ): Promise<import("./types").MetadataUploadResponse> => {
    const form = new FormData();
    form.append("file", file);
    if (glossaryMode) form.append("glossary_mode", glossaryMode);
    if (projectId) form.append("project_id", projectId);
    const res = await fetch(`${BASE}/metadata-curator/upload`, {
      method: "POST",
      body: form,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail || "Upload failed");
    }
    const { session_id } = (await res.json()) as { session_id: string };
    // upload() now only persists + registers the secondary-scan lock; the
    // deferred profiling/sheet-detection read comes back once
    // metadataGetSession() reports the verdict cleared (a no-op wait with
    // no scanner configured, the common case today).
    const session = await pollUntil(async () => {
      const s = await api.metadataGetSession(session_id);
      return s.step === "pending_scan" ? null : s;
    });
    if (session.step === "quarantined") {
      throw new Error(
        "This file was flagged by a secondary malware scan and has been quarantined for review.",
      );
    }
    if (session.step === "error") {
      throw new Error(session.error || "Could not process the uploaded file.");
    }
    return session as unknown as import("./types").MetadataUploadResponse;
  },

  metadataSelectSheets: (
    sessionId: string,
    sheets: string[],
    projectId?: string,
  ) =>
    request<import("./types").MetadataUploadResponse>(
      "/metadata-curator/select-sheets",
      {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          sheets,
          project_id: projectId,
        }),
      },
    ),

  metadataDbConnect: (
    params: import("./types").MetadataDbConnectParams & { project_id?: string },
  ) =>
    request<
      | import("./types").MetadataUploadResponse
      | import("./types").MetadataDbTablesResponse
    >("/metadata-curator/db-connect", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  metadataDbSelectTable: (
    sessionId: string,
    table: string,
    projectId?: string,
  ) =>
    request<import("./types").MetadataUploadResponse>(
      "/metadata-curator/db-select-table",
      {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          table,
          project_id: projectId,
        }),
      },
    ),

  metadataDbSelectTables: (
    sessionId: string,
    tables: string[],
    projectId?: string,
  ) =>
    request<import("./types").MetadataUploadResponse>(
      "/metadata-curator/db-select-tables",
      {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          tables,
          project_id: projectId,
        }),
      },
    ),

  metadataProjectDbTables: (
    projectId: string,
    connectionType: "source" | "target" = "source",
  ) =>
    request<{
      session_id: string;
      source_type: string;
      connection_label: string;
      tables: string[];
      views: string[];
      schema: string;
    }>("/metadata-curator/project-db-tables", {
      method: "POST",
      body: JSON.stringify({
        project_id: projectId,
        connection_type: connectionType,
      }),
    }),

  metadataGetSession: (sessionId: string) =>
    request<import("./types").MetadataSessionState>(
      `/metadata-curator/session/${sessionId}`,
    ),

  metadataEnrich: (
    sessionId: string,
    glossaryMode?: string,
    existingGlossary?: Record<string, string>[],
  ) =>
    request<import("./types").MetadataEnrichResponse>(
      "/metadata-curator/enrich",
      {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          glossary_mode: glossaryMode || "generate",
          existing_glossary: existingGlossary,
        }),
      },
    ),

  metadataClarifications: (sessionId: string) =>
    request<{ clarifications: import("./types").MetadataClarification[] }>(
      `/metadata-curator/clarifications/${sessionId}`,
    ),

  metadataClarify: (params: import("./types").MetadataClarifyRequest) =>
    request<import("./types").MetadataClarifyResponse>(
      "/metadata-curator/clarify",
      {
        method: "POST",
        body: JSON.stringify(params),
      },
    ),

  metadataChat: (
    sessionId: string,
    message: string,
    deterministicOnly?: boolean,
  ) =>
    request<import("./types").MetadataChatResponse>("/metadata-curator/chat", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        message,
        deterministic_only: deterministicOnly || undefined,
      }),
    }),

  metadataUpdateColumn: (
    sessionId: string,
    columnName: string,
    edits: Record<string, unknown>,
  ) =>
    request<{ column: import("./types").MetadataColumnEntry }>(
      `/metadata-curator/column/${sessionId}/${encodeURIComponent(columnName)}`,
      {
        method: "PUT",
        body: JSON.stringify(edits),
      },
    ),

  metadataApproveColumn: (sessionId: string, columnName: string) =>
    request<import("./types").MetadataApproveResponse>(
      "/metadata-curator/approve",
      {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          column_name: columnName,
        }),
      },
    ),

  metadataApproveAll: (sessionId: string, tableName?: string) =>
    request<import("./types").MetadataApproveResponse>(
      "/metadata-curator/approve-all",
      {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          ...(tableName ? { table_name: tableName } : {}),
        }),
      },
    ),

  metadataPublish: (sessionId: string) =>
    request<import("./types").MetadataPublishResponse>(
      "/metadata-curator/publish",
      {
        method: "POST",
        body: JSON.stringify({ session_id: sessionId }),
      },
    ),

  metadataExportUrl: (
    sessionId: string,
    format: string,
    opts?: { tableName?: string; piiOnly?: boolean; minimal?: boolean },
  ): string => {
    const params = new URLSearchParams();
    if (opts?.tableName) params.set("table_name", opts.tableName);
    if (opts?.piiOnly) params.set("pii_only", "true");
    if (opts?.minimal) params.set("minimal", "true");
    const qs = params.toString();
    return `${BASE}/metadata-curator/export/${sessionId}/${format}${qs ? `?${qs}` : ""}`;
  },

  metadataWriteBack: (
    sessionId: string,
    dbConfig: import("./types").MetadataDbConnectParams,
    tableName: string,
  ) =>
    request<import("./types").MetadataWriteBackResponse>(
      "/metadata-curator/write-back",
      {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          db_config: dbConfig,
          table_name: tableName,
        }),
      },
    ),

  metadataVersionHistory: (sessionId: string) =>
    request<{ versions: import("./types").MetadataVersionEntry[] }>(
      `/metadata-curator/versions/${sessionId}`,
    ),

  metadataListSessions: () =>
    request<{ sessions: import("./types").SavedMetadataSession[] }>(
      "/metadata-curator/sessions",
    ),

  metadataResumeSession: (sessionId: string) =>
    request<any>(`/metadata-curator/session/${sessionId}/resume`),

  metadataGenerateDDL: (params: {
    session_id: string;
    table_name?: string;
    table_names?: string[];
    db_type: string;
    ddl_type: string;
    schema_name?: string;
  }) =>
    request<import("./types").DDLResult>("/metadata-curator/generate-ddl", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  metadataSupportedDatabases: () =>
    request<{ databases: import("./types").SupportedDatabase[] }>(
      "/metadata-curator/supported-databases",
    ),

  metadataGetSummary: (sessionId: string) =>
    request<{ summary: string }>(`/metadata-curator/summary/${sessionId}`),

  // The three methods below (published catalog, version history, search) are
  // intentionally ahead of their consumer — the backend endpoints exist and
  // these wrappers match the response shapes, staged for the catalog-browser
  // UI that will call them.
  metadataGetPublishedCatalog: (projectId: string) =>
    request<import("./types").MetadataPublishedCatalog>(
      `/metadata-curator/published/${projectId}`,
    ),

  metadataGetCatalogVersions: (projectId: string) =>
    request<{
      project_id: string;
      versions: import("./types").MetadataCatalogVersion[];
    }>(`/metadata-curator/published/${projectId}/versions`),

  metadataSearchCatalog: (
    projectId: string,
    params: {
      q?: string;
      domain?: string;
      sensitivity?: string;
      table_name?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") qs.set(key, String(value));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<import("./types").MetadataCatalogSearchResult>(
      `/metadata-curator/published/${projectId}/search${suffix}`,
    );
  },

  // ── Backlog Architect Agent (v2: ztd_app/api/modules/backlog) ──
  // Upload parses .txt/.md/.xlsx in the API image; .docx/.pdf return 415
  // pending worker-routed parsing.
  backlogUpload: async (
    projectId: string,
    file: File,
    additionalContext?: string,
  ): Promise<BacklogUploadResult> => {
    const form = new FormData();
    form.append("file", file);
    if (additionalContext) form.append("additional_context", additionalContext);
    const res = await fetch(`${BASE}/v2/projects/${projectId}/backlog/upload`, {
      method: "POST",
      body: form,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail || "Upload failed");
    }
    return res.json();
  },

  // ── v2 backlog (ztd_app/api/modules/backlog) ──
  // The text-only flow is ported to the v2 backend; these call the v2
  // routes (project-scoped create/list, session-scoped steps). The
  // remaining export calls below (Excel/Word/Jira/GitHub) are not yet
  // ported to v2 and still target the legacy /backlog/* paths.
  backlogAnalyzeText: (
    projectId: string,
    text: string,
    additionalContext?: string,
  ) =>
    request<BacklogUploadResponse>(
      `/v2/projects/${projectId}/backlog/analyze-text`,
      {
        method: "POST",
        body: JSON.stringify({ text, additional_context: additionalContext }),
      },
    ),

  backlogGetSession: (sessionId: string) =>
    request<BacklogSessionState>(`/v2/backlog/sessions/${sessionId}`),

  backlogListSessions: (projectId: string) =>
    request<{ sessions: BacklogSessionSummary[] }>(
      `/v2/projects/${projectId}/backlog/sessions`,
    ),

  backlogResumeSession: (sessionId: string) =>
    request<BacklogResumeResponse>(`/v2/backlog/sessions/${sessionId}/resume`),

  backlogDeleteSession: (sessionId: string) =>
    request<{ status: string }>(`/v2/backlog/sessions/${sessionId}`, {
      method: "DELETE",
    }),

  backlogRenameSession: (sessionId: string, name: string) =>
    request<{ status: string; session_name: string }>(
      `/v2/backlog/sessions/${sessionId}/name`,
      {
        method: "PATCH",
        body: JSON.stringify({ name }),
      },
    ),

  backlogClarify: (sessionId: string, answers: Record<string, string>) =>
    request<BacklogClarifyResponse>(
      `/v2/backlog/sessions/${sessionId}/clarify`,
      {
        method: "POST",
        body: JSON.stringify({ answers }),
      },
    ),

  backlogGenerateEpics: (sessionId: string) =>
    request<BacklogEpicsResponse>(
      `/v2/backlog/sessions/${sessionId}/generate-epics`,
      {
        method: "POST",
      },
    ),

  backlogGenerateStories: (
    sessionId: string,
    epicId: string,
    epicFeedback?: string,
    projectRoles?: string[],
  ) =>
    request<BacklogStoriesResponse>(
      `/v2/backlog/sessions/${sessionId}/generate-stories`,
      {
        method: "POST",
        body: JSON.stringify({
          selected_epic_ids: [epicId],
          epic_feedback: epicFeedback,
          project_roles: projectRoles,
        }),
      },
    ),

  backlogGenerateAllStories: (
    sessionId: string,
    epicFeedback?: string,
    selectedEpicIds?: string[],
    projectRoles?: string[],
  ) =>
    request<BacklogStoriesResponse>(
      `/v2/backlog/sessions/${sessionId}/generate-all-stories`,
      {
        method: "POST",
        body: JSON.stringify({
          epic_feedback: epicFeedback,
          selected_epic_ids: selectedEpicIds,
          project_roles: projectRoles,
        }),
      },
    ),

  backlogUpdateStory: (
    sessionId: string,
    storyId: string,
    updates: Partial<BacklogStory>,
  ) =>
    request<{ story: BacklogStory }>(
      `/v2/backlog/sessions/${sessionId}/stories/${storyId}`,
      {
        method: "PUT",
        body: JSON.stringify(updates),
      },
    ),

  backlogDeleteStory: (sessionId: string, storyId: string) =>
    request<{ status: string }>(
      `/v2/backlog/sessions/${sessionId}/stories/${storyId}`,
      {
        method: "DELETE",
      },
    ),

  backlogChat: (sessionId: string, message: string) =>
    request<BacklogChatResponse>(`/v2/backlog/sessions/${sessionId}/chat`, {
      method: "POST",
      body: JSON.stringify({ message }),
    }),

  // Excel + Word both render in-process (synchronous) — same pattern.
  backlogExportExcel: (sessionId: string): string =>
    `${BASE}/v2/backlog/sessions/${sessionId}/export/excel`,

  backlogExportWord: (sessionId: string): string =>
    `${BASE}/v2/backlog/sessions/${sessionId}/export/word`,

  // Poll a backlog document job (document_analyze parse — genuinely async,
  // LLM-driven; word_export job creation is superseded by backlogExportWord
  // above but the job-polling endpoint is shared, so this stays).
  backlogGetJob: (jobId: string) =>
    request<BacklogDocumentJob>(`/v2/backlog/jobs/${jobId}`),

  // Direct download URL for a completed job's artifact (for window.open).
  backlogJobDownloadUrl: (jobId: string): string =>
    `${BASE}/v2/backlog/jobs/${jobId}/download`,

  backlogExportJira: (sessionId: string, config: JiraExportConfig) =>
    request<BacklogExportResult>(
      `/v2/backlog/sessions/${sessionId}/export/jira`,
      {
        method: "POST",
        body: JSON.stringify(config),
      },
    ),

  backlogExportGithub: (sessionId: string, config: GithubExportConfig) =>
    request<BacklogExportResult>(
      `/v2/backlog/sessions/${sessionId}/export/github`,
      {
        method: "POST",
        body: JSON.stringify(config),
      },
    ),

  // ── Data Profiling Agent ─────────────────────────
  profilingUpload: async (file: File, projectId?: string): Promise<any> => {
    const form = new FormData();
    form.append("file", file);
    if (projectId) form.append("project_id", projectId);
    const res = await fetch(`${BASE}/profiling-agent/upload`, {
      method: "POST",
      body: form,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail || "Upload failed");
    }
    return res.json();
  },

  profilingSelectSheets: (sessionId: string, tables: string[]) =>
    request<any>("/profiling-agent/select-sheets", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, tables }),
    }),

  profilingDbConnect: (config: any) =>
    request<any>("/profiling-agent/db-connect", {
      method: "POST",
      body: JSON.stringify(config),
    }),

  profilingSelectTables: (sessionId: string, tables: string[]) =>
    request<any>("/profiling-agent/select-tables", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, tables }),
    }),

  profilingRun: (config: any) =>
    request<any>("/profiling-agent/run", {
      method: "POST",
      body: JSON.stringify(config),
    }),

  profilingStatus: (sessionId: string) =>
    request<any>(`/profiling-agent/status/${sessionId}`),

  profilingResults: (sessionId: string) =>
    request<any>(`/profiling-agent/results/${sessionId}`),

  profilingDq: (sessionId: string) =>
    request<any>(`/profiling-agent/dq/${sessionId}`),

  profilingRelationships: (sessionId: string) =>
    request<any>(`/profiling-agent/relationships/${sessionId}`),

  profilingInterpret: (sessionId: string) =>
    request<any>("/profiling-agent/interpret", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    }),

  profilingChat: (sessionId: string, message: string) =>
    request<any>("/profiling-agent/chat", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, message }),
    }),

  profilingFeedback: (sessionId: string, feedbackType: string, data: any) =>
    request<any>("/profiling-agent/feedback", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        feedback_type: feedbackType,
        ...data,
      }),
    }),

  profilingGetSession: (sessionId: string) =>
    request<any>(`/profiling-agent/session/${sessionId}`),

  profilingShared: (sessionId: string) =>
    request<any>(`/profiling-agent/shared/${sessionId}`),

  // ── GE Suite Review & NL Rules ──
  geRuleUpdate: (
    sessionId: string,
    table: string,
    ruleIndex: number,
    action: string,
  ) =>
    request<any>("/profiling-agent/ge-rules/update", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        table,
        rule_index: ruleIndex,
        action,
      }),
    }),

  geRuleBulkUpdate: (
    sessionId: string,
    table: string,
    action: string,
    indices?: number[],
  ) =>
    request<any>("/profiling-agent/ge-rules/bulk-update", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, table, action, indices }),
    }),

  geRuleAddNatural: (sessionId: string, table: string, ruleText: string) =>
    request<any>("/profiling-agent/ge-rules/add-natural", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        table,
        rule_text: ruleText,
      }),
    }),

  geRulesExport: (sessionId: string, includeRejected: boolean = false) =>
    request<any>("/profiling-agent/ge-rules/export", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        include_rejected: includeRejected,
        format: "json",
      }),
    }),

  geRulesExportExcel: async (
    sessionId: string,
    includeRejected: boolean = false,
  ) => {
    const resp = await fetch(`${BASE}/profiling-agent/ge-rules/export`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      body: JSON.stringify({
        session_id: sessionId,
        include_rejected: includeRejected,
        format: "excel",
      }),
      credentials: "include",
    });
    if (!resp.ok) throw new Error(`Export failed: ${resp.statusText}`);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ge_suite_${sessionId.slice(0, 8)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },

  geRuleEdit: (
    sessionId: string,
    table: string,
    ruleIndex: number,
    kwargs: Record<string, any>,
  ) =>
    request<any>("/profiling-agent/ge-rules/edit", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        table,
        rule_index: ruleIndex,
        kwargs,
      }),
    }),
};

// ── Masking Types ──────────────────────────────────
export interface MaskingTechnique {
  id: string;
  label: string;
  description: string;
  icon: string;
  options: {
    name: string;
    type: string;
    default: any;
    label: string;
    choices?: string[];
  }[];
}

export interface MaskingColumnAnalysis {
  column: string;
  dtype: string;
  sensitivity: string; // none, low, medium, high
  category: string;
  suggested_technique: string | null;
  sample_values: string[];
  null_pct: number;
  unique_count: number;
  total_count: number;
}

export interface MaskingUploadResponse {
  session_id: string;
  file_name?: string;
  source_type?: string;
  table_name?: string;
  is_multi_sheet?: boolean;
  sheets?: MaskingSheetInfo[];
  row_count?: number;
  col_count?: number;
  columns?: MaskingColumnAnalysis[];
  preview?: Record<string, unknown>[];
  step?: string;
  error?: string | null;
}

export interface MaskingSheetInfo {
  name: string;
  row_count_preview: number;
  col_count: number;
  columns: string[];
}

export interface MaskingSelectSheetsResponse {
  session_id: string;
  sheets: MaskingSheetProfile[];
}

export interface MaskingSheetProfile {
  sheet_name: string;
  row_count: number;
  col_count: number;
  columns: MaskingColumnAnalysis[];
  preview: Record<string, unknown>[];
}

export interface MaskingDbTablesResponse {
  session_id: string;
  source_type: string;
  tables: string[];
  schema: string;
}

// ── Multi-table masking (#603) ──────────────────────────────────────
export interface MaskingTableInfo {
  table_name: string;
  row_count: number;
  col_count: number;
  columns: MaskingColumnAnalysis[];
  preview: Record<string, unknown>[];
}

export interface MaskingDbSelectTablesResponse {
  session_id: string;
  tables: MaskingTableInfo[];
}

export interface MaskingPkCandidate {
  columns: string[];
  score: number;
  evidence: string[];
  is_candidate: boolean;
  type: "declared" | "inferred" | "composite";
}

export interface MaskingFkCandidate {
  source_table: string;
  source_columns: string[];
  target_table: string;
  target_columns: string[];
  confidence: number;
  evidence: string[];
  type: "declared" | "inferred";
}

export interface MaskingRelationshipsResponse {
  session_id: string;
  pk_candidates: Record<string, MaskingPkCandidate[]>;
  fk_candidates: MaskingFkCandidate[];
}

export interface MaskingTableConfig {
  table: string;
  column_configs: MaskingColumnConfig[];
}

export interface MaskingDbConnectParams {
  // Required for a fresh manual connect; not needed when connection_id is
  // set instead (resolved server-side) — optional here since the backend
  // takes a raw dict and only enforces "one or the other" at request time,
  // not via strict typing.
  db_type?: string;
  host?: string;
  port?: number;
  database?: string;
  schema_name?: string;
  username?: string;
  password?: string;
  table?: string;
  // Snowflake-specific
  account?: string;
  warehouse?: string;
  role?: string;
  // Reuse one of the project's saved connections instead of full flat
  // credentials (see ztd_app's admin/project_db_config.resolve_connect_config).
  connection_id?: string;
  project_id?: string;
}

export interface MaskingColumnConfig {
  column: string;
  technique: string;
  options: Record<string, any>;
  // Lineage: deterministic mapping across columns/sheets
  lineage_group?: string;
  // Conditional masking: only mask rows matching expression
  filter_expression?: string;
  // Uniqueness: guarantee masked values are unique
  preserve_uniqueness?: boolean;
}

export interface MaskingPreviewResponse {
  session_id: string;
  sheet_name?: string;
  table_name?: string;
  original: Record<string, unknown>[];
  masked: Record<string, unknown>[];
  columns_affected: string[];
  total_rows: number;
  preview_rows: number;
}

export interface MaskingSheetConfig {
  sheet: string;
  column_configs: MaskingColumnConfig[];
}

export interface MaskingExecuteParams {
  session_id: string;
  sheet_configs?: MaskingSheetConfig[];
  table_configs?: MaskingTableConfig[];
  column_configs?: MaskingColumnConfig[];
  pass_through_sheets?: string[];
  target_format: string;
  target_type: string;
  db_config?: MaskingDbConnectParams;
  table_strategy?: string;
  lineage_enabled?: boolean;
  lineage_groups?: string[][];
  date_links?: DateLink[];
}

export interface MaskingExecuteResponse {
  session_id: string;
  rows_processed: number;
  columns_masked: number;
  sheets_processed?: number;
  // Multi-table (#603 items 1/3/4)
  tables_processed?: number;
  is_bundle?: boolean;
  consistency_warnings?: string[];
  target?: string;
  table_name?: string;
  status?: string;
  download_file?: string;
  target_format: string;
  audit: MaskingAudit;
}

// /masking/execute now starts the work in the background and returns
// immediately (avoids CloudFront's OriginReadTimeout killing one long
// request under load) — the UI polls /masking/execute-status instead.
export interface MaskingExecuteStartResponse {
  session_id: string;
  status: "running";
}

export type MaskingExecuteStatusResponse =
  | { session_id: string; status: "running" }
  | { session_id: string; status: "completed"; result: MaskingExecuteResponse }
  | { session_id: string; status: "failed"; error: string };

export interface MaskingAudit {
  total_rows: number;
  total_columns: number;
  columns_masked: number;
  columns_untouched: number;
  masking_details: {
    column: string;
    technique: string;
    technique_label: string;
    options_used: Record<string, any>;
    rows_affected: number;
    total_rows: number;
  }[];
  timestamp: string;
}

export interface MaskingWriteDbParams {
  session_id: string;
  db_config: MaskingDbConnectParams;
  table_strategy?: string;
  table_name?: string;
}

export interface MaskingWriteDbResponse {
  status: string;
  rows_written: number;
  table_name: string;
  message: string;
}

export interface DateLink {
  primary: string;
  linked: string[];
  sheet?: string;
}

export interface MaskingConfigSaveParams {
  name: string;
  description?: string;
  column_configs: MaskingColumnConfig[];
  schema_columns?: string[];
  sheet_configs?: { sheet: string; column_configs: MaskingColumnConfig[] }[];
  is_multi_sheet?: boolean;
  table_configs?: MaskingTableConfig[];
  is_multi_table?: boolean;
  pass_through_sheets?: string[];
  parent_id?: string;
  // Lineage
  lineage_enabled?: boolean;
  lineage_groups?: string[][];
  // Date links
  date_links?: DateLink[];
}

export interface SavedMaskingConfig {
  id: string;
  name: string;
  description: string;
  schema_fingerprint: string;
  schema_columns: string[];
  column_configs: MaskingColumnConfig[];
  sheet_configs?: { sheet: string; column_configs: MaskingColumnConfig[] }[];
  is_multi_sheet?: boolean;
  table_configs?: MaskingTableConfig[];
  is_multi_table?: boolean;
  pass_through_sheets?: string[];
  version?: number;
  parent_id?: string;
  lineage_enabled?: boolean;
  lineage_groups?: string[][];
  date_links?: DateLink[];
  created_at: string;
  _match?: "full" | "partial";
}

export interface MaskingGenerateScriptParams {
  config_name: string;
  column_configs: MaskingColumnConfig[];
  language: "pandas" | "pyspark" | "snowpark";
  deployment_target: "ec2" | "lambda" | "snowflake";
  // Error handling
  error_strategy: "fail_fast" | "log_continue";
  max_errors: number;
  // Logging
  log_level: "DEBUG" | "INFO" | "WARNING";
  log_output: "console" | "file" | "both";
  include_summary: boolean;
  // Data validation
  pre_validation: boolean;
  post_validation: boolean;
  null_handling: "skip" | "placeholder" | "error";
  // Testing
  include_tests: boolean;
  include_sample_data: boolean;
  include_test_runner: boolean;
  // Documentation
  include_code_review: boolean;
  include_changelog: boolean;
  // Lineage (deterministic consistent mapping)
  lineage_enabled: boolean;
  lineage_groups?: string[][];
  // Date preservation
  date_links?: DateLink[];
  // Conditional masking
  include_conditional_masking: boolean;
  // Uniqueness preservation
  include_uniqueness: boolean;
  // Format validation
  include_format_validation: boolean;
  // Audit log
  include_audit_log: boolean;
  // Multi-sheet support
  is_multi_sheet?: boolean;
  sheet_configs?: { sheet: string; column_configs: MaskingColumnConfig[] }[];
  pass_through_sheets?: string[];
  // User custom instructions
  user_instructions?: string;
  // LLM-powered generation
  use_llm?: boolean;
}

export interface LLMTokenInfo {
  model: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
}

export interface LLMTokenEstimate {
  estimated_input_tokens: number;
  estimated_output_tokens: number;
  estimated_total_tokens: number;
  model: string;
  fallback_model: string;
  max_output_tokens: number;
  num_columns: number;
  num_techniques: number;
}

// ── Backlog Architect Types ────────────────────────────
export interface BacklogRequirement {
  id: string;
  type: "functional" | "non-functional" | "constraint" | "assumption";
  title: string;
  description: string;
  source_section: string;
  priority: "must" | "should" | "could" | "wont";
}

export interface BacklogClarification {
  id: string;
  question: string;
  context: string;
  category: "requirement" | "technical" | "scope" | "priority";
  answer?: string;
}

export interface BacklogAcceptanceCriteria {
  id: string;
  scenario?: "happy_path" | "edge_case" | "error_handling" | "boundary";
  given: string;
  when: string;
  then: string;
}

export interface BacklogTask {
  id: string;
  title: string;
  linked_ac: string[];
  subtasks: string[];
}

export interface BacklogStory {
  id: string;
  epic_id: string;
  key: string;
  title: string;
  as_a: string;
  i_want: string;
  so_that: string;
  acceptance_criteria: BacklogAcceptanceCriteria[];
  tasks: BacklogTask[];
  story_points: number;
  estimation_confidence: "high" | "medium" | "low";
  estimation_reasoning: string;
  priority: "must" | "should" | "could";
  dependencies: string[];
  requirement_ids: string[];
  definition_of_ready?: string[];
}

export interface BacklogEpic {
  id: string;
  key: string;
  title: string;
  goal: string;
  definition_of_done?: string;
  requirement_ids: string[];
  stories: BacklogStory[];
  total_points: number;
}

export interface BacklogUploadResponse {
  session_id: string;
  file_name?: string;
  requirements: BacklogRequirement[];
  clarifications: BacklogClarification[];
  document_summary: string;
  requirement_count: {
    functional: number;
    non_functional: number;
    constraints: number;
    assumptions: number;
  };
}

// Upload result: .txt/.md/.xlsx parse synchronously and return the analyze
// payload; .docx/.pdf are worker-routed and return a job id to poll.
export type BacklogUploadResult =
  | ({ mode: "sync" } & BacklogUploadResponse)
  | { mode: "async"; job_id: string; status: string; kind: string };

export interface BacklogSessionState {
  session_id: string;
  step: string;
  requirements: BacklogRequirement[];
  clarifications: BacklogClarification[];
  epics: BacklogEpic[];
  document_summary: string;
  coverage_map?: Record<string, string[]>;
  file_name?: string;
  session_name?: string;
  project_roles?: string[];
  additional_context?: string;
}

export interface BacklogSessionSummary {
  session_id: string;
  session_name: string;
  file_name: string | null;
  step: string;
  epic_count: number;
  story_count: number;
  total_points: number;
  requirement_count: number;
  created_at: string;
  updated_at: string;
}

export interface BacklogResumeResponse {
  session_id: string;
  ui_step: number;
  step: string;
  session_name: string;
  file_name: string | null;
  document_summary: string;
  requirements: BacklogRequirement[];
  clarifications: BacklogClarification[];
  epics: BacklogEpic[];
  coverage_map: Record<string, string[]>;
  project_roles: string[];
  additional_context: string;
  requirement_count: {
    functional: number;
    non_functional: number;
    constraints: number;
    assumptions: number;
  };
}

export interface BacklogClarifyResponse {
  session_id: string;
  requirements: BacklogRequirement[];
  follow_up_clarifications: BacklogClarification[];
  all_clarified: boolean;
}

export interface BacklogEpicsResponse {
  session_id: string;
  epics: BacklogEpic[];
  coverage_map: Record<string, string[]>;
}

export interface BacklogStoriesResponse {
  session_id: string;
  epics: BacklogEpic[];
  total_stories: number;
  total_points: number;
}

export interface BacklogChatResponse {
  response: string;
  updated_epics?: BacklogEpic[];
}

export interface JiraExportConfig {
  base_url: string;
  email: string;
  api_token: string;
  project_key: string;
}

export interface GithubExportConfig {
  token: string;
  owner: string;
  repo: string;
}

export interface BacklogExportResult {
  status: string;
  items_created: number;
  details: { key: string; title: string; url: string }[];
  errors?: string[];
}

// An async backlog document job (worker-rendered Word export / docx-pdf
// upload analysis). Poll until status is "completed" or "failed".
export interface BacklogDocumentJob {
  job_id: string;
  kind: string; // word_export | document_analyze
  status: string; // queued | running | completed | failed | quarantined
  session_id?: string | null;
  result?: Record<string, unknown> | null;
  error_detail?: string | null;
  download_url?: string | null;
}
